import React, { useMemo } from 'react';
import { parseStotram } from './stotramParser';
import { Skeleton, cn } from '../ui';
import { SvaraLine } from './SvaraLine';
import type { LyricsSettings } from './lyricsSettings';
import { buildFinalLines, type LyricLine } from './lyricsTransform';
import { analyzeLyrics, type LyricsAnalysis } from './lyricsDetection';
import { AcrosticText } from './AcrosticText';
import { firstUpper } from './firstUpper';
import { groupDiamondStanzas } from './diamondLayout';

/**
 * The rail is a FIXED 2px and must never scale with `fontSize`, which the reader
 * controls. A rule that grew with the type would become a slab at the top of the
 * range.
 */
const REPEAT_RAIL_WIDTH_PX = 2;
const REPEAT_RAIL_GAP_PX = 14;
/** The section-heading gold at 55%, so the rail reads as subordinate to a heading. */
const REPEAT_RAIL_COLOR = 'rgba(184, 168, 124, 0.55)';
/** `↻` alone announces as nothing useful, so the block carries a spoken label. */
const REPEAT_ACCESSIBILITY_LABEL = 'Repeat the pallavi';

type RenderChunk =
  | { kind: 'line'; line: LyricLine; key: string }
  | { kind: 'repeat'; lines: LyricLine[]; key: string };

/**
 * Collapse each maximal run of `repeat` lines into one chunk so it can be wrapped in
 * a railed block.
 *
 * Deliberately shaped like `groupDiamondStanzas` rather than refactoring this
 * component onto stanzas — that would disturb `activeLineIndex` scroll targets and
 * the diamond grouping for no gain here.
 */
function groupRepeatBlocks(lines: LyricLine[]): RenderChunk[] {
  const chunks: RenderChunk[] = [];
  let run: LyricLine[] = [];

  const flush = () => {
    if (run.length) {
      chunks.push({ kind: 'repeat', lines: run, key: `repeat-${run[0].index}` });
      run = [];
    }
  };

  for (const line of lines) {
    if (line.repeat) {
      run.push(line);
    } else {
      flush();
      chunks.push({ kind: 'line', line, key: `line-${line.index}` });
    }
  }
  flush();

  return chunks;
}

export interface LyricsRendererProps {
  text: string | null;
  isLoading?: boolean;
  fontSize?: number;
  align?: 'left' | 'center';
  activeLineIndex?: number;
  /** Beat pattern from the bhajan's talam — drives svara group alignment. */
  beatPattern?: string | null;
  /** User toggles. Defaults to "show everything as-is". */
  settings?: LyricsSettings;
  /**
   * Diamond/acrostic analysis of `text`. Optional — callers that already need it for
   * other chrome (BhajanDetails, for the "Did You Know?" card) pass theirs in, and
   * everyone else gets it computed here. Computing it here rather than substituting
   * the text at the call site is deliberate: distraction mode renders the same raw
   * lyrics through this component and must never leak `[diamond]` tags either.
   */
  lyricsAnalysis?: LyricsAnalysis;
  /**
   * When the lyrics have a `[diamond]` section, show the diamond (vajra) layout
   * instead of the normal lyrics. Defaults on, matching jnana-native's useState(true).
   */
  showingDiamondView?: boolean;
  /**
   * Language of `text`. Drives section-heading detection — the lyrics arrive in the
   * reader's own script, and a Latin-only heading regex finds nothing in the other
   * nine — and supplies the word for the repeated-pallavi label.
   */
  languageCode?: string;
  className?: string;
}

export const LyricsRenderer: React.FC<LyricsRendererProps> = ({
  text,
  isLoading,
  fontSize = 22,
  align = 'left',
  activeLineIndex,
  beatPattern,
  settings,
  lyricsAnalysis,
  showingDiamondView = true,
  languageCode = 'en',
  className,
}) => {
  const analysis = useMemo(
    () => lyricsAnalysis ?? analyzeLyrics(text ?? ''),
    [lyricsAnalysis, text]
  );

  const diamondMode = analysis.hasDiamondSection && showingDiamondView;
  // Without a diamond section `normalLyrics` IS the raw text, so plain bhajans go
  // through this component byte-for-byte unchanged.
  const effectiveText = diamondMode ? analysis.diamondLyrics : analysis.normalLyrics;
  // No permission gate on the web: whenever a bhajan has %markers% they are stripped
  // from the text and rendered as highlighted syllables.
  const acrosticMode = analysis.hasAcrosticMarkers;

  // Lyrics arrive in the reader's language, so the parser needs it too — its
  // uvāca/dhyānam headings are matched per-script (see stotramParser).
  const stotram = useMemo(
    () => (effectiveText ? parseStotram(effectiveText, languageCode) : null),
    [effectiveText, languageCode]
  );

  /** Apply user settings to produce the final ordered line stream for rendering. */
  const finalLines = useMemo(
    () => (effectiveText ? buildFinalLines(effectiveText, settings, languageCode) : []),
    [effectiveText, settings, languageCode]
  );

  if (isLoading) {
    return (
      <div className={cn('space-y-3', className)}>
        {[88, 72, 92, 60, 80, 95, 70, 85, 65, 90, 75, 88].map((w, i) => (
          <Skeleton key={i} width={`${w}%`} height={fontSize + 4} />
        ))}
      </div>
    );
  }

  if (!text) {
    return <p className="text-text-tertiary italic">No lyrics available.</p>;
  }

  const baseStyle = { fontSize, lineHeight: 1.7 };
  // `overflow-wrap` is inherited, so one class on each root covers lines, headings and
  // verse numbers alike. Needed because the reader can take the type up to 65px, where
  // a single transliterated word is wider than a phone — without this the whole
  // document scrolls sideways rather than the word breaking.
  const wrapClass = 'break-words';
  // The diamond layout is centred regardless of the `align` prop — distraction mode
  // passes align="left" and would otherwise render a left-aligned diamond.
  const centered = diamondMode || align === 'center';
  const alignClass = centered ? 'text-center' : 'text-left';

  // Section-heading style — matches jnana-native's secondaryForegroundColor (#B8A87C),
  // italic, light weight (300), ~1.55x the lyric font size.
  const sectionStyle: React.CSSProperties = {
    color: '#B8A87C',
    fontSize: Math.round(fontSize * 1.55),
    lineHeight: 1.25,
    letterSpacing: '-0.01em',
  };

  // Diamond verse number — same gold as the section headings, italic and dimmed, in a
  // fixed gutter to the right of the stanza. Native uses a 40px gutter at its 16px
  // lyric size; the web lyric size is user-adjustable, so the gutter scales with it
  // (22px default → 40px, the same as native) instead of clipping at large sizes.
  const verseNumberStyle: React.CSSProperties = {
    color: '#B8A87C',
    fontSize: Math.round(fontSize * 1.55),
    fontStyle: 'italic',
    opacity: 0.7,
    width: Math.round(fontSize * 1.8),
  };

  if (stotram?.isStotram && !diamondMode) {
    return (
      <div className={cn('space-y-6', alignClass, wrapClass, className)} style={baseStyle}>
        {stotram.sections.map((s, i) => {
          if (s.kind === 'header') {
            return (
              <h3 key={i} className="font-sans italic font-light mb-3" style={sectionStyle}>
                {acrosticMode ? <AcrosticText text={s.label ?? ''} isAcrosticMode /> : s.label}
              </h3>
            );
          }
          return (
            <div key={i} className="relative pl-6">
              {s.number != null && (
                <span
                  className="absolute -left-1 top-0 font-mono text-text-tertiary"
                  style={{ fontSize: fontSize - 6 }}
                  aria-label={`Shloka ${s.number}`}
                >
                  {s.number}.
                </span>
              )}
              <div className="space-y-1 font-display">
                {s.lines.map((line, j) => (
                  <p key={j} className="text-text-primary">
                    {acrosticMode ? (
                      <AcrosticText text={firstUpper(line) || ' '} isAcrosticMode />
                    ) : (
                      firstUpper(line) || ' '
                    )}
                  </p>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const renderLine = (l: LyricLine) => {
    if (l.kind === 'blank') return <div key={l.index} className="h-3" aria-hidden />;
    if (l.kind === 'section') {
      if (l.repeat) {
        // A returning pallavi: the section word one rung down the scale, stepped
        // back, carrying the return sign. `l.text` is the heading as it appears in
        // the lyrics, so it is already in the reader's script.
        return (
          <h3
            key={l.index}
            aria-label={REPEAT_ACCESSIBILITY_LABEL}
            className="mb-3 font-sans italic font-light"
            style={{
              color: '#B8A87C',
              fontSize: Math.round(fontSize * 1.25),
              lineHeight: 1.25,
              letterSpacing: '-0.01em',
              opacity: 0.85,
            }}
          >
            {`↻ ${l.text}`}
          </h3>
        );
      }
      // A line only classifies as `section` when it matches the anchored heading
      // regex — a `%` anywhere in it prevents the match. So section headings can
      // never carry acrostic markers and need no AcrosticText here.
      return (
        <h3
          key={l.index}
          className="mt-8 mb-3 font-sans italic font-light"
          style={sectionStyle}
        >
          {l.text}
        </h3>
      );
    }
    if (l.kind === 'svara') {
      // Svara lines are note tokens, never lyric syllables; native likewise renders
      // them without acrostic parsing and without verse-number extraction.
      return (
        <SvaraLine
          key={l.index}
          text={l.text}
          beatPattern={beatPattern}
          fontSize={fontSize - 4}
        />
      );
    }
    return (
      <p
        key={l.index}
        id={`lyrics-line-${l.index}`}
        aria-current={activeLineIndex === l.index ? 'true' : undefined}
        className={cn(
          'transition-colors duration-150 flex items-baseline gap-3',
          activeLineIndex === l.index ? 'text-accent-gold' : 'text-text-primary',
          centered && 'justify-center'
        )}
      >
        {/*
          `min-w-0` is what actually lets a long word break at large sizes. The line is
          a flex container, and a flex item defaults to `min-width: auto` — it sizes to
          max-content and so never presents the renderer with a line box too narrow to
          fit, leaving the inherited `overflow-wrap: break-word` with nothing to do. At
          65px a long transliterated word is wider than a phone, and (centred) it
          spilled off BOTH edges.
        */}
        {acrosticMode ? (
          <AcrosticText className="min-w-0" text={firstUpper(l.text) || ' '} isAcrosticMode />
        ) : (
          <span className="min-w-0">{firstUpper(l.text) || ' '}</span>
        )}
        {l.number != null && (
          <span className="font-mono text-text-tertiary shrink-0" style={{ fontSize: fontSize - 8 }}>
            {l.number}
          </span>
        )}
      </p>
    );
  };

  if (diamondMode) {
    return (
      <div className={cn(alignClass, 'space-y-1.5', wrapClass, className)} style={baseStyle}>
        {groupDiamondStanzas(finalLines).map((chunk) => {
          if (chunk.kind === 'line') return renderLine(chunk.line);
          const content = chunk.lines.map(renderLine);
          if (chunk.verseNumber == null) {
            return (
              <div key={chunk.key} className="space-y-1.5">
                {content}
              </div>
            );
          }
          return (
            <div key={chunk.key} className="flex items-center">
              <div className="flex-1 min-w-0 space-y-1.5">{content}</div>
              <span
                className="shrink-0 text-center"
                style={verseNumberStyle}
                aria-label={`Verse ${chunk.verseNumber}`}
              >
                {chunk.verseNumber}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={cn(alignClass, 'space-y-1.5', wrapClass, className)} style={baseStyle}>
      {groupRepeatBlocks(finalLines).map((chunk) =>
        chunk.kind === 'line' ? (
          renderLine(chunk.line)
        ) : (
          <div
            key={chunk.key}
            data-repeat-block="true"
            className="space-y-1.5"
            style={
              // Centred lyrics drop the rail — a left rule fights centred text.
              centered
                ? { marginTop: 24, marginBottom: 8 }
                : {
                    borderLeftWidth: REPEAT_RAIL_WIDTH_PX,
                    borderLeftStyle: 'solid',
                    borderLeftColor: REPEAT_RAIL_COLOR,
                    paddingLeft: REPEAT_RAIL_GAP_PX,
                    marginTop: 24,
                    marginBottom: 8,
                  }
            }
          >
            {chunk.lines.map(renderLine)}
          </div>
        )
      )}
    </div>
  );
};
